import urlcat from 'urlcat';
import urlData from './nice-url-data';
import lang from '../lang/lang';
import * as R from 'ramda';
import { getKey, getValue, valueIsNotNull, isMinimumOrMaximum, isMinimum, isNullOrEmpty, isNotNullOrEmpty, parseQueryString } from './url-utils';
const NOT_FIXED_STRING_FIELDS = ['keyWord', 'comissionId'];
const NUMERIC_FIELDS = ['page'];
const keys = urlData[lang.locale].keys;
const values = urlData[lang.locale].values;

const stateToUrlStringFixed = R.cond([
	[
		valueIsNotNull,
		R.applySpec({
			key: getKey(keys),
			value: getValue(values)
		})
	]
]);
const stateToUrlString = R.cond([
	[
		valueIsNotNull,
		R.applySpec({
			key: getKey(keys),
			value: R.nthArg(1)
		})
	]
]);

const stateToUrlNumeric = R.cond([
	[
		valueIsNotNull,
		R.applySpec({
			key: getKey(keys),
			value: R.nthArg(1)
		})
	]
]);
const stateToUrlMinimum = (key, value, params) => {
	const STRING_VALUES_PARAMS = ['floorsInBuilding'];
	const valueName = R.replace('Minimum', '', key);
	const maxName = `${valueName}Maximum`;
	const maxValue = params[maxName];
	const convertedValue = STRING_VALUES_PARAMS.includes(valueName) ? values[valueName][value] : value;
	const convertedMaxValue = STRING_VALUES_PARAMS.includes(valueName) ? values[valueName][maxValue] : maxValue;
	const isNotPlusValue = R.complement(R.pipe(R.defaultTo(''), R.last, R.equals('+')));
	if (R.and(R.and(isNotNullOrEmpty(maxValue), isNotNullOrEmpty(value)), isNotPlusValue(maxValue))) {
		return {
			key: keys[valueName],
			value: `${convertedValue}~${convertedMaxValue}`
		};
	} else if (isNotNullOrEmpty(value)) {
		return {
			key: keys[valueName],
			value: `${convertedValue}~`
		};
	}
};
const stateToUrlMaximum = (key, value, params) => {
	const valueName = R.replace('Maximum', '', key);
	const minName = `${valueName}Minimum`;
	const minValue = params[minName];
	if (isNullOrEmpty(minValue) && isNotNullOrEmpty(value)) {
		return {
			key: keys[valueName],
			value: `~${value}`
		};
	}
};
const stateToUrlRange = R.ifElse(isMinimum, stateToUrlMinimum, stateToUrlMaximum);

const stateToUrlSelectedTags = R.curry((value, key) => {
	const concatProps = item => R.join('|', [item.value, item.type]);
	const mapConcatProps = R.map(concatProps);
	const mergedValue = R.join('&', mapConcatProps(value));
	return {
		key: keys[key],
		value: mergedValue
	};
});
const stateToUrlArrayGeneral = R.curry((value, key) => {
	const getTranslatedValue = getValue(values)(key);
	const mapTranslated = R.map(getTranslatedValue);
	const mergedValue = R.pipe(mapTranslated, R.join('|'));
	return {
		key: keys[key],
		value: mergedValue(value)
	};
});
const stateToUrlArray = (key, value) => {
	return R.cond([
		[R.equals('selectedTags'), stateToUrlSelectedTags(value)],
		[R.T, stateToUrlArrayGeneral(value)]
	])(key);
};
const stateToUrlBoolean = R.cond([
	[
		R.pipe(R.nthArg(1), R.equals(true)),
		R.applySpec({
			key: getKey(keys),
			value: R.nthArg(1)
		})
	]
]);

const stateToUrl = (key, value, params) => {
	if (isMinimumOrMaximum(key)) {
		return stateToUrlRange(key, value, params);
	} else if (R.is(Boolean, value)) {
		return stateToUrlBoolean(key, value);
	} else if (typeof value == 'string') {
		return R.includes(key, NOT_FIXED_STRING_FIELDS) ? stateToUrlString(key, value) : stateToUrlStringFixed(key, value);
	} else if (typeof value == 'number') {
		return stateToUrlNumeric(key, value, params);
	} else if (Array.isArray(value) && R.length(value)) {
		return stateToUrlArray(key, value);
	}
};

const reverseKeys = R.reduce((acc, item) => {
	acc[keys[item]] = item;
	return acc;
}, {});
const reverseValues = R.reduce((acc, item) => {
	const valuesforType = values[item];
	const reversedValueGroup = R.reduce(
		(acc2, item2) => {
			acc2[valuesforType[item2]] = item2;
			return acc2;
		},
		{},
		R.keys(valuesforType)
	);
	acc[item] = reversedValueGroup;
	return acc;
}, {});

const urlToStateSelectedTags = R.pipe(
	R.split('&'),
	R.map(
		R.pipe(
			R.split('|'),
			R.applySpec({
				value: R.pipe(R.nth(0), R.trim),
				type: R.nth(1)
			})
		)
	)
);

const urlToStateRange = (baseKey, value, reversedValues, state) => {
	const STRING_VALUES_PARAMS = ['floorsInBuilding'];
	const isStringValue = R.includes(baseKey, STRING_VALUES_PARAMS);
	const newState = R.clone(state);
	const [min, max] = R.split('~', value);
	const minValue = isStringValue ? getValue(reversedValues)(baseKey, min) : parseInt(min, 10);
	const maxValue = isStringValue ? getValue(reversedValues)(baseKey, max) : parseInt(max, 10);
	newState[`${baseKey}Minimum`] = R.defaultTo(null, minValue);
	newState[`${baseKey}Maximum`] = R.defaultTo(null, maxValue);
	return newState;
};
const urlToStateNumeric = (key, value, obj) => {
	return R.assoc(key, parseInt(value, 10), obj);
};
const urlToStateBoolean = (key, obj) => {
	return R.assoc(key, true, obj);
};
const urlToStateArray = (key, value, reversedValues) => {
	const getTranslatedValue = translateKey => item => reversedValues[translateKey][item];
	return R.pipe(R.split('|'), R.map(getTranslatedValue(key)))(value);
};
export default {
	createUrlFromState(params) {
		const paramsArray = R.toPairs(params);
		const niceUrlParams = R.reduce(
			(acc, item) => {
				const convertedValue = stateToUrl(item[0], item[1], params);
				//TODO Ramda
				if (R.not(R.isNil(convertedValue))) {
					acc[convertedValue.key] = convertedValue.value;
				}
				return acc;
			},
			{},
			paramsArray
		);

		const utmParams = R.pickBy((_, key) => key.startsWith('utm'), params);
		const finalUrlParams = { ...niceUrlParams, ...utmParams };
		let url = urlcat('/kereses', finalUrlParams);
		return url;
	},
	createStateFromUrl() {
		const SIMPLE_SEARCH_KEYS = ['price', 'saleType', 'propertyType', 'area', 'selectedTags', 'advancedSearchVisible', 'order', 'page'];
		const FIXED_VALUE_PROPERTIES = ['saleType', 'propertyType', 'order'];
		const ARRAY_TYPES = [
			'conditionOfProperty',
			'heating',
			'buildingStructure',
			'comfortLevel',
			'staircaseType',
			'livingRoomOrientation',
			'view',
			'parkingOption',
			'innerHeight'
		];

		const urlObj = parseQueryString(window.location.search);
		const reversedKeys = reverseKeys(R.keys(keys));
		const reversedValues = reverseValues(R.keys(values));
		const stateObj = R.reduce(
			(acc, item) => {
				if (item.startsWith('utm')) {
					acc.utmParams[item] = urlObj[item];
					return acc;
				}
				const isSimple = R.includes(reversedKeys[item], SIMPLE_SEARCH_KEYS);
				let advancedOrSimpleObj = isSimple ? acc.simple : acc.advanced;
				if (R.includes(reversedKeys[item], FIXED_VALUE_PROPERTIES)) {
					advancedOrSimpleObj = R.assoc(reversedKeys[item], reversedValues[reversedKeys[item]][urlObj[item]], advancedOrSimpleObj);
				} else if (R.includes(reversedKeys[item], NOT_FIXED_STRING_FIELDS)) {
					advancedOrSimpleObj = R.assoc(reversedKeys[item], R.trim(urlObj[item]), advancedOrSimpleObj);
				} else if (R.includes(reversedKeys[item], ARRAY_TYPES)) {
					advancedOrSimpleObj = R.assoc(reversedKeys[item], urlToStateArray(reversedKeys[item], urlObj[item], reversedValues), advancedOrSimpleObj);
				} else if (R.includes('~', urlObj[item])) {
					advancedOrSimpleObj = urlToStateRange(reversedKeys[item], urlObj[item], reversedValues, advancedOrSimpleObj);
				} else if (R.equals('selectedTags', reversedKeys[item])) {
					advancedOrSimpleObj = R.assoc(reversedKeys[item], urlToStateSelectedTags(urlObj[item]), advancedOrSimpleObj);
				} else if (R.equals('true', urlObj[item])) {
					advancedOrSimpleObj = urlToStateBoolean(reversedKeys[item], advancedOrSimpleObj);
				} else if (R.includes(reversedKeys[item], NUMERIC_FIELDS)) {
					advancedOrSimpleObj = urlToStateNumeric(reversedKeys[item], urlObj[item], advancedOrSimpleObj);
				}

				if (isSimple) {
					acc.simple = advancedOrSimpleObj;
				} else {
					acc.advanced = advancedOrSimpleObj;
				}
				return acc;
			},
			{ advanced: {}, simple: {}, utmParams: {} },
			R.keys(urlObj)
		);
		return stateObj;
	}
};
